import React from 'react'
import {FormattedNumber, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Card} from '@vio/atlas/Card'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {selectIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {pointsProgram} from '@vio/core/src/loyalty/pointsProgram'

import {BrandLogo} from '../BrandLogo'
import {loyaltyMessages} from '../loyaltyMessages'

const TooltipTrigger = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
`

// Placeholder until product writes the copy; a plain string so translators do not get it yet
const tierLevelsExplanation = 'General explanation'

const TierLevelsTooltip = ({
  placement,
  children
}: {
  placement: TooltipPlacement
  children: React.ReactNode
}) => {
  const {formatMessage} = useIntl()
  const tierLevelsTitle = formatMessage(loyaltyMessages.tierLevelsTitle)

  return (
    <Tooltip
      placement={placement}
      title={tierLevelsTitle}
      content={tierLevelsExplanation}
    >
      <TooltipTrigger type="button" aria-label={tierLevelsTitle}>
        {children}
      </TooltipTrigger>
    </Tooltip>
  )
}

const TierLabel = ({variant}: {variant: 'labelXS' | 'labelS'}) => {
  const {brand} = useBrand()
  const {pointsTierMessage} = brand.messages

  if (!pointsTierMessage) return null

  return (
    <Text variant={variant}>
      <FormattedMessageWrapper {...pointsTierMessage} />
    </Text>
  )
}

const Figure = styled(Text)`
  display: block;
`

const BalanceText = styled(Text)`
  &.--to-line-end {
    text-align: end;
  }
`

const Balance = ({alignItems}: {alignItems: 'center' | 'flex-end'}) => (
  <Box display="flex" flexDirection="column" alignItems={alignItems} flex="1">
    <BalanceText
      variant="labelS"
      colorPath="content.brand.primary"
      className={cx(alignItems === 'flex-end' && '--to-line-end')}
    >
      <Figure variant="title2XL" colorPath="content.brand.primary">
        <FormattedNumber value={pointsProgram.balance} />
      </Figure>
      <FormattedMessageWrapper {...loyaltyMessages.pointsCollected} />
    </BalanceText>
  </Box>
)

const MemberNumberRow = styled(Text)`
  display: flex;
  justify-content: space-between;
  gap: ${cssTheme.layout.spacing.s400};
`

/** The tier, the balance and the member number in the desktop user menu */
export const PointsProgramBadge = () => {
  const {monetisation} = useBrandCapabilities()
  const isAuthenticated = useSelector(selectIsAuthenticated)

  if (!monetisation.hasPointsProgram || !isAuthenticated) return null

  return (
    <div data-id="PointsProgramBadge">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="s400"
        padding="s500"
        backgroundColor="background.brand.tertiary"
      >
        <Box display="flex" alignItems="center" gap="s250">
          <TierLabel variant="labelXS" />
          <TierLevelsTooltip placement={TooltipPlacement.BottomEnd}>
            <Icon name="Information" size="sm" />
          </TierLevelsTooltip>
        </Box>
        <Balance alignItems="center" />
      </Box>
      <Divider />
      <Box
        paddingTop="s400"
        paddingBottom="s400"
        paddingLeft="s500"
        paddingRight="s500"
      >
        <MemberNumberRow variant="bodyXS" colorPath="content.neutral.secondary">
          <FormattedMessageWrapper {...loyaltyMessages.memberNumber} />
          <span>{pointsProgram.memberNumber}</span>
        </MemberNumberRow>
      </Box>
      <Divider />
    </div>
  )
}

/** The member number as a tag under the member's name or avatar */
export const PointsProgramMemberTag = () => (
  <Tag variant="brand" size="sm" dataId="PointsProgramMemberTag">
    <FormattedMessageWrapper
      {...loyaltyMessages.memberNumberShort}
      values={{number: pointsProgram.memberNumber}}
    />
  </Tag>
)

const Programme = () => {
  const {brand} = useBrand()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap="s200"
      paddingTop="s300"
      paddingBottom="s300"
      paddingLeft="s200"
      paddingRight="s200"
    >
      <BrandLogo brand={brand} height={16} />
      <Text variant="labelXS" colorPath="content.brand.primary">
        {pointsProgram.programName}
      </Text>
    </Box>
  )
}

/** The programme card: logo and balance, with the tier and its levels underneath */
export const PointsProgramCard = () => (
  <Card
    borderColorPath="border.neutral.secondary"
    backgroundColorPath="background.brand.tertiary"
    borderRadius="sm"
    dataId="PointsProgramCard"
  >
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      padding="s500"
    >
      <Programme />
      <Balance alignItems="flex-end" />
    </Box>
    <Divider />
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      paddingTop="s200"
      paddingBottom="s200"
      paddingLeft="s500"
      paddingRight="s250"
    >
      <TierLabel variant="labelXS" />
      <TierLevelsTooltip placement={TooltipPlacement.BottomEnd}>
        <Box padding="s200" display="flex">
          <Icon name="Information" size="md" />
        </Box>
      </TierLevelsTooltip>
    </Box>
  </Card>
)
